import { useEffect, useState } from 'react';

import { emailValidation } from '@eneco/common/validation/emailRegex';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { patchBasket, sendOfferByMail } from '@eneco/dc/services';
import useDC from '@eneco/dc/useDC';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { generateBasketIdCookieName } from '@eneco/flows/utils/FlowUtils';
import { typedYupResolver } from '@eneco/hooks/form';
import {
  Box,
  Button,
  Checkbox,
  Form,
  Heading,
  Image,
  InputCombobox,
  InputEmail,
  NotificationBox,
  Stack,
  Text,
} from '@eneco/sparky';
import { useFeatureValue } from '@growthbook/growthbook-react';
import {
  DC_Domain_Models_Products_ProductType,
  DC_Domain_Models_Products_ShoppingBasket_BasketOffer,
  DC_Domain_Models_Products_ShoppingBasket_OrderInfo,
  DC_Domain_Models_Products_ShoppingBasket_Usages,
} from '@monorepo-types/dc';
import { mapImage, useLayoutData } from '@sitecore/common';
import { Fields } from '@sitecore/types/manual/smeFlowExperiment/SMEOfferStep';
import { FlowsNewEnergyOfferStep } from '@sitecore/types/SMEOfferStep';
import { useMachine } from '@xstate/react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useDebounce } from 'react-use';
import * as yup from 'yup';

import { FlowContextSme } from '../../context';
import {
  constructTextualDataKey,
  generateReturnFromEmailParam,
  getCostDetails,
  getDurationInMonth,
  getTitle,
} from '../../helpers';
import { chamberOfCommerceSearchMachine } from '../../machines';
import { Offer, Product } from '../../types';

type FormValues = { C1: boolean; C3: boolean; companyName: string; emailAddress: string };

interface Props {
  textualData?: Fields;
  availableOffers: Offer[];
}

const getReturnUrl = (params: Record<string, string>) => {
  if (typeof window === 'undefined') return '';

  const url = new URL(window.location.href);
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });
  return `${url.protocol}//${url.host}${url.pathname}${url.search}`;
};

export const EMAIL_OFFER_FEATURE_ID = '586666|sme-send-offer';

export const EmailOfferDialogComponent = ({ textualData, availableOffers }: Props) => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextSme>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const { businessUnit, label } = useDC();

  const basketId = useFlowSelector(state => state.context.basketId);
  const energyType = useFlowSelector(state => state.context.energyType);
  const emailAddress = useFlowSelector(state => state.context.emailAddress);
  const companyName = useFlowSelector(state => state.context.companyName);
  const usageElectricityHigh = useFlowSelector(state => state.context.usageElectricityHigh);
  const usageElectricityLow = useFlowSelector(state => state.context.usageElectricityLow);
  const solarPanelsOutput = useFlowSelector(state => state.context.solarPanelsOutput);
  const usageGas = useFlowSelector(state => state.context.usageGas);
  const street = useFlowSelector(state => state.context.street);
  const houseNumber = useFlowSelector(state => state.context.houseNumber);
  const houseNumberSuffix = useFlowSelector(state => state.context.houseNumberSuffix);
  const postalCode = useFlowSelector(state => state.context.postalCode);
  const city = useFlowSelector(state => state.context.city);
  const hasResidencePurpose = useFlowSelector(state => state.context.hasResidencePurpose);
  const isPersonalDetailsEditProhibited = useFlowSelector(state => state.context.isPersonalDetailsEditProhibited);
  const [isOpen, setIsOpen] = useState(false);
  const [sendOfferError, setSendOfferError] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const getProduct = (product: Product) => {
    return {
      code: product.code,
      type: product.type,
      costDetails: getCostDetails(product),
      productName: product.title,
      durationInMonths: product.contractSpan ? getDurationInMonth(product.contractSpan) : 0,
    };
  };

  const getProducts = (offer: Offer) => {
    const products = offer.products.map(getProduct);

    if (offer.costDiscounts.productDetails) {
      // V3 cashback products report the amount under `cashBackDirect`/`cashBackOnYearNote`, not `oneOffCosts`.
      const costDetails = offer.costDiscounts.productDetails.costDetails?.find(
        item => item.type === 'oneOffCosts' || item.type === 'cashBackDirect' || item.type === 'cashBackOnYearNote',
      );
      if (costDetails) {
        const cashbackProduct = {
          type: 'cashback' as DC_Domain_Models_Products_ProductType,
          productName: offer.costDiscounts.productDetails.description ?? '',
          // `marketingProductCode` can be empty for cashback products - fall back to the internal code.
          code:
            offer.costDiscounts.productDetails.marketingProductCode ||
            offer.costDiscounts.productDetails.internalDcMarketingProductCode ||
            '',
          costDetails: [
            {
              type: 'oneOffCosts',
              costs: costDetails.costs,
            },
          ],
          durationInMonths: 0,
        };
        products.push(cashbackProduct);
      }
    }

    return products;
  };

  const offerDetails = (offer: Offer) => {
    const textualDataKey = constructTextualDataKey(offer.contractSpan, energyType);
    const offerName = getTitle(textualDataKey, textualData?.productData?.productDataList as FlowsNewEnergyOfferStep);

    return {
      offerName,
      durationInMonths: offer.contractSpan ? getDurationInMonth(offer.contractSpan[0]) : 0,
      costDetails: getCostDetails(offer),
      products: getProducts(offer),
    };
  };

  const [chamberOfCommerceSearchState, sendTochamberOfCommerceSearchMachine] =
    useMachine(chamberOfCommerceSearchMachine);

  const results = chamberOfCommerceSearchState?.context?.results;
  const companyNameOptions: string[] = [
    ...new Set((results?.map((result: { name?: string }) => result?.name ?? '') ?? []) as string[]),
  ];

  const formSchema = yup.object({
    companyName: yup.string().required(textualData?.emailOffer?.companyNameFormField?.value?.requiredMessage),
    emailAddress: emailValidation({
      emailMessage: textualData?.emailOffer?.emailAddressFormField?.value?.validationMessage,
      requiredMessage: textualData?.emailOffer?.emailAddressFormField?.value?.requiredMessage,
    }),
  });

  const formMethods = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      emailAddress,
      companyName,
      C1: false,
      C3: false,
    },
    resolver: typedYupResolver<FormValues>(formSchema),
  });

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = formMethods;

  const watchCompanyName = watch('companyName');

  useDebounce(
    () => {
      if (!watchCompanyName) return;
      const isCompanyNameInOptions = companyNameOptions.includes(watchCompanyName);
      if (watchCompanyName && !isCompanyNameInOptions) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const payload: any = {
          type: 'GET_COMPANY_OPTIONS',
          config: { businessUnit, label },
          values: {
            companyName: watchCompanyName,
          },
        };
        sendTochamberOfCommerceSearchMachine(payload);
      }
    },
    500,
    [watchCompanyName],
  );
  const {
    route: { fields },
  } = useLayoutData();

  const handleSendOfferViaEmail = async (
    offers: DC_Domain_Models_Products_ShoppingBasket_BasketOffer[],
    companyName: string,
    emailAddress: string,
  ) => {
    const basketProperties: {
      usages: DC_Domain_Models_Products_ShoppingBasket_Usages;
      /* `orderReferenceId` is not available for this shopping basket usage, even though it is required in the OrderInfo interface. The orderInfo is used to retrieve the delivery address and organization details, to prefill the email template. */
      orderInfo: Omit<DC_Domain_Models_Products_ShoppingBasket_OrderInfo, 'orderReferenceId'>;
      offers: DC_Domain_Models_Products_ShoppingBasket_BasketOffer[];
    } = {
      usages: {
        electricityHigh: usageElectricityHigh,
        electricityLow: usageElectricityLow,
        electricityRedelivery: solarPanelsOutput,
        gas: usageGas,
      },
      orderInfo: {
        deliveryAddress: {
          street,
          houseNumber,
          houseNumberSuffix,
          postalCode,
          city,
          type: 'Delivery' as const,
          hasResidentialPurpose: hasResidencePurpose,
        },
        organisation: {
          kvkName: companyName,
        },
      },
      offers,
    };

    try {
      if (!basketId) {
        throw new Error('Basket ID is required');
      }
      setSendingEmail(true);

      // Update the shopping basket with the offer and user details, so that it can be used in the email template.
      await patchBasket({
        requestBody: {
          // @ts-ignore-next-line because the orderInfo expects orderReferenceId to be define, however it is not applicable to this case.
          data: {
            ...basketProperties,
          },
        },
        id: basketId,
        businessUnit,
        label,
      });
      const returnParams = {
        [generateBasketIdCookieName(fields?.flowName?.value?.toString() || 'smeenergyflow')]: basketId || '',
        companyName,
        ...generateReturnFromEmailParam(),
      };

      await sendOfferByMail({
        requestBody: {
          data: {
            emailAddress: emailAddress,
            shoppingBasketId: basketId,
            url: getReturnUrl(returnParams),
          },
        },
        businessUnit,
        label,
      });
      setEmailSent(true);
      setSendingEmail(false);
    } catch {
      setSendOfferError(true);
      setSendingEmail(false);
    }
  };

  const submitForm: SubmitHandler<FormValues> = ({ companyName, emailAddress, C1, C3 }) => {
    const offers = availableOffers.map(offerDetails) as DC_Domain_Models_Products_ShoppingBasket_BasketOffer[];
    void handleSendOfferViaEmail(offers, companyName, emailAddress);
    sendToFlowMachine({
      type: 'UPDATE_VALUES',
      values: {
        emailAddress,
        companyName,
        C1,
        C3,
      },
    });
  };

  useEffect(() => {
    !isOpen && reset();
  }, [isOpen, reset]);

  return (
    <TrackedDialog
      onClose={() => {
        setIsOpen(false);
      }}
      setOpen={() => setIsOpen(true)}
      isOpen={isOpen}
      title={!emailSent && textualData?.emailOffer?.emailDialog?.value?.title}
      trigger={<Button action="secondary">{textualData?.emailOffer?.emailDialog?.value?.triggerText}</Button>}>
      {emailSent ? (
        <SubmissionSuccessContent textualData={textualData} onClose={() => setIsOpen(false)} />
      ) : (
        <FormProvider {...formMethods}>
          <Form
            onSubmit={e => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(submitForm)(e);
            }}>
            <Stack direction="column" gap="6">
              {sendOfferError && (
                <NotificationBox
                  isAlert={false}
                  variant={textualData?.emailOffer?.errorEmailNotification?.value?.variant}
                  title={textualData?.emailOffer?.errorEmailNotification?.value?.title}
                  text={<RichText html={textualData?.emailOffer?.errorEmailNotification?.value.content} />}
                />
              )}
              <Stack.Item>
                <InputCombobox
                  {...register('companyName')}
                  defaultValue={companyName}
                  onChange={value => {
                    const newValue = value.trim();
                    if (newValue !== watchCompanyName) {
                      setValue('companyName', newValue);
                    }
                  }}
                  label={textualData?.emailOffer?.companyNameFormField?.value?.label ?? ''}
                  placeholder={textualData?.emailOffer?.companyNameFormField?.value?.placeholder}
                  hint={textualData?.emailOffer?.companyNameFormField?.value?.hint}
                  error={errors?.companyName?.message}
                  options={
                    chamberOfCommerceSearchState.matches('FETCHING')
                      ? [textualData?.emailOffer?.companySearchLoadingText?.value ?? '']
                      : companyNameOptions
                  }
                />
              </Stack.Item>
              <Stack.Item>
                <InputEmail
                  {...register('emailAddress')}
                  label={textualData?.emailOffer?.emailAddressFormField?.value?.label ?? ''}
                  placeholder={textualData?.emailOffer?.emailAddressFormField?.value?.placeholder}
                  hint={textualData?.emailOffer?.emailAddressFormField?.value?.hint}
                  autoComplete="email"
                  error={errors?.emailAddress?.message}
                />
              </Stack.Item>
              {!isPersonalDetailsEditProhibited && (
                <Stack.Item>
                  <Stack direction="column" gap="2">
                    <Checkbox
                      label={textualData?.emailOffer?.productOffersFirstPartyCheckbox?.value?.label ?? ''}
                      {...register('C1')}
                    />
                    <Checkbox
                      label={textualData?.emailOffer?.productOffersThirdPartyCheckbox?.value?.label ?? ''}
                      {...register('C3')}
                    />
                  </Stack>
                </Stack.Item>
              )}
              <Stack.Item>
                <Button type="submit" isLoading={sendingEmail}>
                  {textualData?.emailOffer?.emailDialog?.value?.submitButtonText}
                </Button>
              </Stack.Item>
            </Stack>
          </Form>
        </FormProvider>
      )}
    </TrackedDialog>
  );
};

export const EmailOfferDialog = (props: Props) => {
  const enabled = useFeatureValue(EMAIL_OFFER_FEATURE_ID, false);

  return enabled ? <EmailOfferDialogComponent {...props} /> : null;
};

interface SubmissionSuccessContentProps extends Pick<Props, 'textualData'> {
  onClose: () => void;
}
const SubmissionSuccessContent = ({ textualData, onClose }: SubmissionSuccessContentProps) => {
  const imageProps = mapImage(textualData?.emailOffer?.thankYouImage);

  return (
    <Box>
      <Stack gap="2">
        {imageProps?.src && (
          <Stack alignX="center">
            <Image src={imageProps.src} alt={imageProps?.alt ?? ''} height="auto" objectFit="cover" />
          </Stack>
        )}
        <Heading as="h3" size="M">
          {textualData?.emailOffer?.thankYouTitle?.value}
        </Heading>
        <Text size="BodyL">{textualData?.emailOffer?.thankYouContent?.value}</Text>
      </Stack>
      <Box paddingTop="6">
        <Button onClick={onClose} action="primary">
          {textualData?.emailOffer?.emailSentCloseButtonText?.value}
        </Button>
      </Box>
    </Box>
  );
};
